import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import StarRating from '../components/StarRating';
import './Reviews.css';

const thMonths = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];

function formatThaiDate(s) {
    if (!s) return '';
    const d = new Date(s);
    if (isNaN(d.getTime())) return '';
    return `${d.getDate()} ${thMonths[d.getMonth()]} ${d.getFullYear() + 543}`;
}

function Reviews() {
    const { user, isAdmin } = useAuth();
    const [summary, setSummary] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState('');

    const emailVerified = !!user?.email_verified;
    const admin = typeof isAdmin === 'function' ? isAdmin() : false;

    const load = useCallback(async () => {
        try {
            const [s, l] = await Promise.all([
                api.get('/reviews/summary'),
                api.get('/reviews?limit=200')
            ]);
            setSummary(s.data);
            setReviews(l.data.reviews || []);
            if (s.data.myReview) {
                setRating(s.data.myReview.rating);
                setComment(s.data.myReview.comment || '');
                setDisplayName(s.data.myReview.display_name || '');
            }
        } catch (e) {
            setMsg('โหลดรีวิวไม่สำเร็จ');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const submit = async () => {
        if (!rating) { setMsg('กรุณาเลือกจำนวนดาว'); return; }
        setSaving(true);
        setMsg('');
        try {
            await api.post('/reviews', { rating, comment, display_name: displayName });
            setMsg('บันทึกรีวิวเรียบร้อย ขอบคุณครับ 💙');
            await load();
        } catch (e) {
            if (e.response?.status === 403) setMsg('กรุณายืนยันอีเมลก่อนรีวิว');
            else setMsg(e.response?.data?.error || 'บันทึกไม่สำเร็จ');
        } finally {
            setSaving(false);
        }
    };

    const removeMine = async () => {
        if (!window.confirm('ต้องการลบรีวิวของคุณใช่หรือไม่?')) return;
        try {
            await api.delete('/reviews/me');
            setRating(0);
            setComment('');
            setDisplayName('');
            setMsg('ลบรีวิวของคุณแล้ว');
            await load();
        } catch (e) {
            setMsg('ลบไม่สำเร็จ');
        }
    };

    const adminDelete = async (id) => {
        if (!window.confirm('ลบรีวิวนี้ (แอดมิน) ใช่หรือไม่?')) return;
        try {
            await api.delete(`/reviews/${id}`);
            await load();
        } catch (e) {
            setMsg('ลบไม่สำเร็จ');
        }
    };

    if (loading) {
        return (
            <div className="flex-center" style={{ minHeight: '40vh' }}>
                <div className="spinner"></div>
            </div>
        );
    }

    const total = summary?.count || 0;
    const avg = summary?.average || 0;
    const dist = summary?.distribution || { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };

    return (
        <div className="reviews-page">
            <h1 className="reviews-title">รีวิวระบบ iMoney</h1>

            {/* สรุปภาพรวม */}
            <div className="reviews-summary-card">
                <div className="rs-overall">
                    <div className="rs-avg-num">{avg.toFixed(1)}</div>
                    <StarRating value={Math.round(avg)} readOnly size={22} />
                    <div className="rs-total">จาก {total} รีวิว</div>
                </div>
                <div className="rs-bars">
                    {[5, 4, 3, 2, 1].map((star) => {
                        const c = dist[star] || 0;
                        const pct = total ? Math.round((c / total) * 100) : 0;
                        return (
                            <div className="rs-bar-row" key={star}>
                                <span className="rs-bar-label">{star} ดาว</span>
                                <div className="rs-bar-track">
                                    <div className="rs-bar-fill" style={{ width: `${pct}%` }}></div>
                                </div>
                                <span className="rs-bar-count">{c}</span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* เขียน/แก้ไขรีวิวของฉัน */}
            <div className="reviews-form-card">
                <h2>{summary?.myReview ? 'รีวิวของคุณ' : 'เขียนรีวิว'}</h2>
                {!emailVerified ? (
                    <div className="reviews-gate">
                        ⭐ ต้องยืนยันอีเมลก่อน จึงจะให้คะแนนและรีวิวได้ —{' '}
                        <Link to="/profile#email">ไปยืนยันอีเมล</Link>
                    </div>
                ) : (
                    <>
                        <div className="rf-stars">
                            <StarRating value={rating} onChange={(n) => { setRating(n); setMsg(''); }} size={36} />
                        </div>
                        <input
                            className="rf-name-input"
                            type="text"
                            placeholder="ชื่อที่จะแสดงบนรีวิว (เช่น ชื่อเล่น) — ไม่บังคับ"
                            maxLength={100}
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                        />
                        <textarea
                            className="rf-textarea"
                            placeholder="เล่าประสบการณ์การใช้งานระบบ (ไม่บังคับ)"
                            maxLength={1000}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                        />
                        <div className="rf-actions">
                            <button className="rf-save" onClick={submit} disabled={saving || !rating}>
                                {saving ? 'กำลังบันทึก...' : (summary?.myReview ? 'อัปเดตรีวิว' : 'ส่งรีวิว')}
                            </button>
                            {summary?.myReview && (
                                <button className="rf-delete" onClick={removeMine} disabled={saving}>
                                    ลบรีวิวของฉัน
                                </button>
                            )}
                        </div>
                    </>
                )}
                {msg && <div className="rf-msg">{msg}</div>}
            </div>

            {/* รายการรีวิวทั้งหมด */}
            <h2 className="reviews-list-title">รีวิวทั้งหมด ({total})</h2>
            {reviews.length === 0 ? (
                <div className="reviews-empty">ยังไม่มีรีวิว เป็นคนแรกที่ให้คะแนนกันเถอะ ⭐</div>
            ) : (
                <div className="reviews-list">
                    {reviews.map((rv) => {
                        const mine = rv.user_id === user?.id;
                        return (
                            <div className={`review-item ${mine ? 'mine' : ''}`} key={rv.id}>
                                <div className={`review-avatar ${rv.avatar_url ? 'has-image' : ''}`}>
                                    {rv.avatar_url ? (
                                        <img src={`/api${rv.avatar_url}`} alt="ผู้รีวิว" />
                                    ) : (
                                        <svg className="review-avatar-default" viewBox="0 0 24 24" aria-hidden="true">
                                            <path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10zm0 2.5c-4.33 0-8 2.17-8 5V21h16v-1.5c0-2.83-3.67-5-8-5z" />
                                        </svg>
                                    )}
                                </div>
                                <div className="review-body">
                                    <div className="review-top">
                                        <span className="review-name">
                                            {rv.display_name
                                                ? rv.display_name
                                                : <span className="review-anon">ผู้ใช้ระบบ</span>}
                                            {mine && <span className="review-mine-tag">รีวิวของคุณ</span>}
                                        </span>
                                        <span className="review-date">{formatThaiDate(rv.updated_at || rv.created_at)}</span>
                                    </div>
                                    <StarRating value={rv.rating} readOnly size={16} />
                                    {rv.comment && <p className="review-comment">{rv.comment}</p>}
                                </div>
                                {admin && (
                                    <button
                                        className="review-admin-del"
                                        title="ลบรีวิว (แอดมิน)"
                                        onClick={() => adminDelete(rv.id)}
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default Reviews;
